import React, { useState, useEffect } from 'react';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Modal from 'components/Tvendo/Common/ModalBase';
import { FileUploader } from 'react-drag-drop-files';
import { WithApp } from 'contexts/appContext';
import { wsGetAll, wsCreate } from 'services/WService.js';

// Prefijo fijo del N° de serie. Si alguna vez cambia, se toca solo acá.
const PREFIJO_SERIE = 'Troya';

const fileTypes = ['JPG', 'PNG'];

/**
 * Mantenimiento del catálogo de muebles.
 *
 * El proveedor no se pide: siempre es Arcor y lo pone el backend.
 *
 * El estado tampoco se elige acá. Entre Asignado y Almacén lo mueve el
 * control de muebles, y la baja tiene su propio botón en el listado. Igual
 * se manda en dataForce: al crear entra como Almacén, y al editar se
 * reenvía el que ya tenía para no pisarlo.
 */
export default WithApp(function ModalFurniture(props) {
  const furniture = props.model;
  const esNuevo = !furniture?.id;

  const [categories, setCategories] = useState([]);
  const [dataChanging, setDataChanging] = useState({});
  const [codigo, setCodigo] = useState('');
  const [serie, setSerie] = useState('');
  const [estado, setEstado] = useState('F');

  const [fotoUrl, setFotoUrl] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [version, setVersion] = useState(1);

  function handleChange(field, value) {
    setDataChanging((prev) => ({ ...prev, [field]: value }));
  }

  async function loadCategories() {
    let response = await wsGetAll('base/furniture-category');
    if (response.success) setCategories(response.data);
  }

  /**
   * Correlativos sugeridos para el alta: el mayor número que ya existe más uno,
   * a tres dígitos. El del código y el de la serie se calculan por separado,
   * cada uno mirando su propia columna, para que uno no arrastre al otro.
   */
  async function sugerirCorrelativos() {
    let response = await wsGetAll('base/furniture');
    if (!response.success) return;

    const siguiente = (valores) =>
      String(valores.reduce((n, v) => Math.max(n, parseInt(v, 10) || 0), 0) + 1).padStart(3, '0');

    setCodigo(siguiente(response.data.map((f) => f.code)));

    //solo cuentan las series que ya usan el prefijo; las viejas no siguen la forma
    setSerie(
      PREFIJO_SERIE +
        '-' +
        siguiente(
          response.data
            .map((f) => f.serial_number || '')
            .filter((s) => s.startsWith(PREFIJO_SERIE + '-'))
            .map((s) => s.slice(PREFIJO_SERIE.length + 1))
        )
    );
  }

  //al abrir para otro registro se limpia lo que no viene del modelo
  useEffect(() => {
    setDataChanging({});
    setFotoUrl(furniture?.file_url || null);
    setEstado(furniture?.state || 'F');
  }, [furniture]);

  useEffect(() => {
    if (!furniture.open) return;
    loadCategories();
    //al editar se respetan los que ya tiene; el correlativo es solo para el alta
    if (furniture?.id) {
      setCodigo(furniture.code || '');
      setSerie(furniture.serial_number || '');
    } else {
      sugerirCorrelativos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [furniture.open]);

  //recién cuando llegó el combo se puede preseleccionar, y comparando como texto
  useEffect(() => {
    if (categories.length > 0 && furniture?.base_furniture_category_id) {
      handleChange('base_furniture_category_id', String(furniture.base_furniture_category_id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, furniture]);

  const categorySelect =
    categories.find((c) => String(c.id) === String(dataChanging.base_furniture_category_id)) || null;

  //la foto va aparte del guardado: necesita que el mueble ya exista
  async function subirFoto(file) {
    let fd = new FormData();
    fd.append('photo', file);

    setSubiendo(true);
    let res = await wsCreate(`base/furniture/${furniture.id}/photo`, fd);
    setSubiendo(false);

    if (res.success) {
      //el ?v= fuerza al navegador a recargarla: la URL no cambia al reemplazarla
      setFotoUrl(`${res.data.file_url}?v=${version}`);
      setVersion((v) => v + 1);
      props.showNotify(res.message);
      props.reload();
    } else {
      props.showNotify(res.message, 'error');
    }
  }

  return (
    <Modal
      title="Mueble"
      model={furniture}
      setOpen={props.setOpen}
      showNotify={props.showNotify}
      reload={props.reload}
      entity="base/furniture"
      dataForce={{
        base_furniture_category_id: dataChanging.base_furniture_category_id || null,
        state: estado
      }}
    >
      <TextField
        label="Descripción"
        type="text"
        name="description"
        margin="normal"
        fullWidth
        defaultValue={furniture?.description || ''}
        required
      />

      <TextField
        label="Código"
        type="text"
        name="code"
        margin="normal"
        fullWidth
        value={codigo}
        onChange={(e) => setCodigo(e.target.value)}
        inputProps={{ maxLength: 20 }}
        helperText="Identifica al mueble físico, no se puede repetir"
        required
      />

      <TextField
        label="N° de serie"
        type="text"
        name="serial_number"
        margin="normal"
        fullWidth
        value={serie}
        onChange={(e) => setSerie(e.target.value)}
        inputProps={{ maxLength: 40 }}
        required
      />

      <Autocomplete
        options={categories}
        value={categorySelect}
        getOptionLabel={(item) => (item.code ? `${item.code} - ${item.description}` : '')}
        getOptionSelected={(option, val) => String(option.id) === String(val.id)}
        renderInput={(params) => (
          <TextField {...params} label="Categoría" margin="normal" fullWidth />
        )}
        onChange={(e, item) => handleChange('base_furniture_category_id', item?.id)}
      />


      {/* La foto necesita que el mueble exista para colgársela */}
      {esNuevo ? (
        <p style={{ color: '#9a9a9a', marginTop: 16 }}>
          La foto se agrega volviendo a abrir el mueble, una vez guardado.
        </p>
      ) : (
        <div style={{ marginTop: 16 }}>
          <p style={{ marginBottom: 6 }}>Foto del mueble</p>

          {fotoUrl && !subiendo && (
            <div style={{ maxWidth: '260px', marginBottom: '10px' }}>
              <img src={fotoUrl} alt="Mueble" style={{ width: '100%', borderRadius: 6 }} />
            </div>
          )}

          {subiendo && (
            <div style={{ width: '60px', height: '60px', marginBottom: '10px' }}>
              <div className="lds-dual-ring"></div>
            </div>
          )}

          <FileUploader
            handleChange={subirFoto}
            types={fileTypes}
            disabled={subiendo}
            label="Has click o arrastra una imagen aquí"
            hoverTitle="Suelta la imagen aquí"
            maxSize={2}
          />
        </div>
      )}
    </Modal>
  );
});
